import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Building2, Globe, Loader2, MapPin, Save } from 'lucide-react';
import { normalizeOperationAddressValue } from './operationAddressUtils';

const OperationAddressBlock = ({
  title,
  description,
  value,
  onChange,
  onSave,
  saving = false,
  showSaveButton = true,
  compact = false
}) => {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState({
    countries: false,
    cities: false,
    districts: false
  });

  const addressValue = useMemo(() => normalizeOperationAddressValue(value), [value]);

  const updateAddress = (patch) => {
    onChange?.({
      ...addressValue,
      ...patch
    });
  };

  useEffect(() => {
    let active = true;

    const fetchCountries = async () => {
      setLoading((current) => ({ ...current, countries: true }));
      try {
        const { data } = await axios.get('/api/locations/countries');
        if (!active) return;
        setCountries(Array.isArray(data) ? data : []);
      } catch {
        if (active) setCountries([]);
      } finally {
        if (active) {
          setLoading((current) => ({ ...current, countries: false }));
        }
      }
    };

    fetchCountries();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!addressValue.countryId) {
      setCities([]);
      setDistricts([]);
      setLoading((current) => ({ ...current, cities: false, districts: false }));
      return undefined;
    }

    const fetchCities = async () => {
      setLoading((current) => ({ ...current, cities: true }));
      try {
        const { data } = await axios.get(`/api/locations/cities/${addressValue.countryId}`);
        if (!active) return;
        setCities(Array.isArray(data) ? data : []);
      } catch {
        if (active) setCities([]);
      } finally {
        if (active) {
          setLoading((current) => ({ ...current, cities: false }));
        }
      }
    };

    fetchCities();

    return () => {
      active = false;
    };
  }, [addressValue.countryId]);

  useEffect(() => {
    let active = true;

    if (!addressValue.cityId) {
      setDistricts([]);
      setLoading((current) => ({ ...current, districts: false }));
      return undefined;
    }

    const fetchDistricts = async () => {
      setLoading((current) => ({ ...current, districts: true }));
      try {
        const { data } = await axios.get(`/api/locations/districts/${addressValue.cityId}`);
        if (!active) return;
        setDistricts(Array.isArray(data) ? data : []);
      } catch {
        if (active) setDistricts([]);
      } finally {
        if (active) {
          setLoading((current) => ({ ...current, districts: false }));
        }
      }
    };

    fetchDistricts();

    return () => {
      active = false;
    };
  }, [addressValue.cityId]);

  return (
    <section className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{description || 'Adres'}</p>
          <h2 className="mt-1 text-lg font-black tracking-tight text-slate-900">{title}</h2>
        </div>
        {showSaveButton && onSave ? (
          <button
            type="button"
            onClick={() => onSave(addressValue)}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Kaydet
          </button>
        ) : null}
      </div>

      <div className={`mt-5 grid gap-4 ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2'}`}>
        <label className="space-y-1 md:col-span-1">
          <span className="ml-1 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <Globe size={12} />
            Ülke {loading.countries && <Loader2 size={10} className="animate-spin text-blue-500" />}
          </span>
          <select
            value={addressValue.countryId || ''}
            onChange={(event) => updateAddress({
              countryId: event.target.value,
              cityId: '',
              districtId: ''
            })}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          >
            <option value="">Ülke seçin</option>
            {countries.map((country) => (
              <option key={country._id} value={country._id}>
                {country.name}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 md:col-span-1">
          <span className="ml-1 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <Building2 size={12} />
            Şehir {loading.cities && <Loader2 size={10} className="animate-spin text-blue-500" />}
          </span>
          <select
            value={addressValue.cityId || ''}
            onChange={(event) => updateAddress({
              cityId: event.target.value,
              districtId: ''
            })}
            disabled={!addressValue.countryId}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all disabled:cursor-not-allowed disabled:opacity-50 focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          >
            <option value="">Şehir seçin</option>
            {cities.map((city) => (
              <option key={city._id} value={city._id}>
                {city.name}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 md:col-span-2">
          <span className="ml-1 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <MapPin size={12} />
            İlçe / Bölge {loading.districts && <Loader2 size={10} className="animate-spin text-blue-500" />}
          </span>
          <select
            value={addressValue.districtId || ''}
            onChange={(event) => updateAddress({
              districtId: event.target.value
            })}
            disabled={!addressValue.cityId}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all disabled:cursor-not-allowed disabled:opacity-50 focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          >
            <option value="">İlçe / bölge seçin</option>
            {districts.map((district) => (
              <option key={district._id} value={district._id}>
                {district.name} {district.isCustomsPoint ? '(Gümrük)' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 md:col-span-2">
          <span className="ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
            Açık Adres
          </span>
          <textarea
            value={addressValue.fullAddress || ''}
            onChange={(event) => updateAddress({ fullAddress: event.target.value })}
            rows="4"
            placeholder="Sokak, bina, kat, kapı no gibi serbest adres bilgisi"
            className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          />
        </label>
      </div>
    </section>
  );
};

export default OperationAddressBlock;
